import { groupPackageSessions } from '@borradh-workspace/web-shared';
import { format } from 'date-fns';
import {
  CalendarClock,
  Circle,
  CircleCheck,
  Clock,
  Package,
} from 'lucide-react';

import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  type ClientPackage,
  type ClientPackageStatus,
  clientPackageStatusLabels,
} from '@borradh-workspace/api-client/types';
import { useChangeClientPackage, useClientPackages } from '../api';
import { formatMoney } from '../lib/format-money';

function statusVariant(
  status: ClientPackageStatus
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'active':
      return 'default';
    case 'pending':
      return 'secondary';
    case 'cancelled':
    case 'expired':
      return 'destructive';
    default:
      return 'outline';
  }
}

/** "Valid until 30 Dec 2026", "Valid for 90 days from payment", or no expiry. */
function validityLine(pkg: ClientPackage): { label: string; value: string } {
  if (pkg.expiresAt)
    return {
      label: 'Valid until',
      value: format(new Date(pkg.expiresAt), 'd MMM yyyy'),
    };
  if (pkg.status === 'pending' && pkg.validityDays)
    return {
      label: 'Valid for',
      value: `${pkg.validityDays} days from payment`,
    };
  return { label: 'Validity', value: 'No expiry' };
}

function PackageCard({
  pkg,
  leadId,
}: {
  pkg: ClientPackage;
  leadId: string;
}) {
  const { changeClientPackage, isChanging } = useChangeClientPackage({
    leadId,
  });
  const canCancel = pkg.status === 'pending' || pkg.status === 'active';
  // Paid in clinic only: an online checkout still open settles it itself.
  const canMarkPaid = pkg.status === 'pending' && !pkg.awaitingOnlinePayment;
  const closed = pkg.status === 'cancelled' || pkg.status === 'expired';
  const validity = validityLine(pkg);

  return (
    <Card className={cn('gap-0 py-0', closed && 'opacity-75')}>
      <CardContent className="space-y-4 p-5">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Package className="size-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <p className="truncate font-semibold">{pkg.name}</p>
              <Badge variant={statusVariant(pkg.status)} className="text-xs">
                {clientPackageStatusLabels[pkg.status]}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">
                {formatMoney(pkg.priceCents, pkg.currency)}
              </span>
              {pkg.listPriceCents > pkg.priceCents && (
                <span className="ml-2 line-through">
                  {formatMoney(pkg.listPriceCents, pkg.currency)}
                </span>
              )}
            </p>
          </div>
        </div>

        {pkg.awaitingOnlinePayment && (
          <p className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
            <Clock className="size-4 shrink-0" />
            Activates when the online payment is made.
          </p>
        )}

        <div className="space-y-2">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-muted-foreground">Sessions left</span>
            <span className="font-medium tabular-nums">
              {pkg.servicesRemaining} of {pkg.servicesTotal}
            </span>
          </div>
          <Progress
            aria-label="Sessions left"
            value={
              pkg.servicesTotal > 0
                ? (pkg.servicesRemaining / pkg.servicesTotal) * 100
                : 0
            }
          />
        </div>

        <ul className="divide-y rounded-lg border">
          {groupPackageSessions(pkg.items).map(({ item, total, remaining }) => (
            <li
              key={item.id}
              className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm"
            >
              <span className="flex min-w-0 items-center gap-2.5">
                {item.used ? (
                  <CircleCheck className="size-4 shrink-0 text-primary" />
                ) : (
                  <Circle className="size-4 shrink-0 text-muted-foreground/60" />
                )}
                <span
                  className={cn(
                    'truncate',
                    item.used && 'text-muted-foreground'
                  )}
                >
                  {item.serviceName}
                </span>
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {total > 1
                  ? `${remaining} of ${total} sessions left`
                  : item.used
                    ? 'Used'
                    : 'Not used yet'}
              </span>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <CalendarClock className="size-4 shrink-0" />
            {validity.label}{' '}
            <span className="font-medium text-foreground">
              {validity.value}
            </span>
          </p>
          {(canMarkPaid || canCancel) && (
            <div className="flex gap-2">
              {canMarkPaid && (
                <Button
                  disabled={isChanging}
                  onClick={() =>
                    changeClientPackage({
                      clientPackageId: pkg.id,
                      action: 'activate',
                    })
                  }
                  size="sm"
                >
                  Mark paid
                </Button>
              )}
              {canCancel && (
                <ConfirmDeleteDialog
                  cancelLabel="Keep package"
                  confirmLabel="Cancel package"
                  description="The services left on it can no longer be used. Refund any payment in Stripe. This can’t be undone."
                  isPending={isChanging}
                  onConfirm={() =>
                    changeClientPackage({
                      clientPackageId: pkg.id,
                      action: 'cancel',
                    })
                  }
                  title={<>Cancel “{pkg.name}” for this client?</>}
                  trigger={
                    <Button disabled={isChanging} size="sm" variant="outline">
                      Cancel package
                    </Button>
                  }
                />
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function ClientPackagesTab({ leadId }: { leadId: string }) {
  const { packages, isLoading, isError } = useClientPackages(leadId);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1].map((n) => (
          <Skeleton key={n} className="h-56 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Failed to load packages.
      </p>
    );
  }

  if (packages.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Package />
          </EmptyMedia>
          <EmptyTitle>No packages yet</EmptyTitle>
          <EmptyDescription>
            Packages this customer buys show here, with the services they have
            left.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="space-y-3">
      {packages.map((pkg) => (
        <PackageCard key={pkg.id} pkg={pkg} leadId={leadId} />
      ))}
    </div>
  );
}
